import { useEffect } from 'react';
import { Box } from '@mui/material';
import {
  CORE_FONTS,
  DISPLAY_FONTS,
  Font,
  googleFontsHref,
} from '@porter/shared-theme';
import { LINE_MID, resetButton, ring } from './constants';

interface Props {
  fontId: string | null;
  onSelect: (id: string) => void;
}

const ALL_FONTS = [...CORE_FONTS, ...DISPLAY_FONTS];

/** The picker previews every family, so it loads all of them up front. */
function useFontPreviews() {
  useEffect(() => {
    const links = ALL_FONTS.map(font => {
      const href = googleFontsHref(font.id);
      if (document.head.querySelector(`link[href="${href}"]`)) return null;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
      return link;
    });
    return () => links.forEach(link => link?.remove());
  }, []);
}

function FontRow({
  font,
  size,
  hint,
  selected,
  onSelect,
}: {
  font: Font;
  size: number;
  hint: string;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onSelect(font.id)}
      aria-pressed={selected}
      sx={{
        ...resetButton,
        position: 'relative',
        border: `1px solid ${LINE_MID}`,
        borderRadius: '8px',
        padding: '11px 14px',
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 1,
        '&::after': ring(selected),
      }}>
      <Box
        sx={{
          fontFamily: font.stack,
          fontSize: size,
          fontWeight: 700,
          color: 'text.primary',
        }}>
        {font.name}
      </Box>
      <Box sx={{ fontSize: 11, color: 'text.tertiary', flex: 'none' }}>
        {hint}
      </Box>
    </Box>
  );
}

function FontPicker({ fontId, onSelect }: Props) {
  useFontPreviews();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Box sx={{ fontSize: 12, fontWeight: 700, color: 'text.tertiary' }}>
        Readable
      </Box>
      <Box
        sx={{
          margin: '0 0 2px',
          fontSize: 12,
          lineHeight: 1.45,
          color: 'text.secondary',
        }}>
        Readable fonts set headings and body copy alike, so your whole site
        shares one family.
      </Box>
      {CORE_FONTS.map(font => (
        <FontRow
          key={font.id}
          font={font}
          size={19}
          hint="Headings and body"
          selected={font.id === fontId}
          onSelect={onSelect}
        />
      ))}
      <Box
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: 'text.tertiary',
          marginTop: '6px',
        }}>
        Display
      </Box>
      <Box
        sx={{
          margin: '0 0 2px',
          fontSize: 12,
          lineHeight: 1.45,
          color: 'text.secondary',
        }}>
        Display fonts set headings only. Body copy stays in a readable core font
        so long text is still legible.
      </Box>
      {DISPLAY_FONTS.map(font => (
        <FontRow
          key={font.id}
          font={font}
          size={21}
          hint="Headings only"
          selected={font.id === fontId}
          onSelect={onSelect}
        />
      ))}
    </Box>
  );
}

export default FontPicker;
